import { CHARACTER_ACTIONS } from "@/character/actions";
import { normalizeGridTuning, type GridTuning } from "@/character/draft";

/** Sprite frames per motion (columns 2–7 on the sheet). */
export const GRID_COLUMNS = 6;
export const GRID_ROWS = CHARACTER_ACTIONS.length;

/** Full sheet: column 1 = label, columns 2–7 = sprites. */
export const SHEET_COLUMNS = GRID_COLUMNS + 1;

export const TEMPLATE_FRAME_WIDTH = 96;
export const TEMPLATE_FRAME_HEIGHT = 96;

export const TEMPLATE_SHEET_WIDTH = TEMPLATE_FRAME_WIDTH * SHEET_COLUMNS;
export const TEMPLATE_SHEET_HEIGHT = TEMPLATE_FRAME_HEIGHT * GRID_ROWS;

/** Cropped sprite-only sheet (no label column). */
export const SPRITE_SHEET_WIDTH = TEMPLATE_FRAME_WIDTH * GRID_COLUMNS;
export const SPRITE_SHEET_HEIGHT = TEMPLATE_SHEET_HEIGHT;

export interface CharacterGridSpec {
  /** Sprite frame columns (always 6). */
  columns: number;
  rows: number;
  frameWidth: number;
  frameHeight: number;
  /** X offset where sprite frames start (one cell when sheet has label column). */
  regionX: number;
  regionY: number;
  /** Total columns on the uploaded sheet (6 = sprites only, 7 = label + sprites). */
  sheetColumns: number;
  /** Manual nudge so grid lines match the uploaded art (px). */
  alignX: number;
  alignY: number;
}

export function defaultGridSpec(): CharacterGridSpec {
  return {
    columns: GRID_COLUMNS,
    rows: GRID_ROWS,
    frameWidth: TEMPLATE_FRAME_WIDTH,
    frameHeight: TEMPLATE_FRAME_HEIGHT,
    regionX: TEMPLATE_FRAME_WIDTH,
    regionY: 0,
    sheetColumns: SHEET_COLUMNS,
    alignX: 0,
    alignY: 0,
  };
}

export function normalizeGridSpec(grid: CharacterGridSpec): CharacterGridSpec {
  const regionX = grid.regionX ?? 0;
  const regionY = grid.regionY ?? 0;
  const sheetColumns =
    grid.sheetColumns ?? (regionX > 0 ? grid.columns + 1 : grid.columns);
  return {
    ...grid,
    regionX,
    regionY,
    sheetColumns,
    alignX: grid.alignX ?? 0,
    alignY: grid.alignY ?? 0,
  };
}

export function inferGridFromImageSize(
  width: number,
  height: number,
): CharacterGridSpec {
  const sevenCol = trySevenColumnSheet(width, height);
  if (sevenCol) return sevenCol;

  const sixCol = trySixColumnSheet(width, height);
  if (sixCol) return sixCol;

  throw new Error(
    `ขนาดรูปต้องหารเป็นกริดได้ · เทมเพลต ${TEMPLATE_SHEET_WIDTH}×${TEMPLATE_SHEET_HEIGHT} (7 คอลัมน์) หรือสไปรต์ล้วน ${SPRITE_SHEET_WIDTH}×${SPRITE_SHEET_HEIGHT} (6 คอลัมน์)`,
  );
}

function trySevenColumnSheet(width: number, height: number): CharacterGridSpec | null {
  if (width % SHEET_COLUMNS !== 0 || height % GRID_ROWS !== 0) {
    return null;
  }
  const cell = width / SHEET_COLUMNS;
  const rowH = height / GRID_ROWS;
  if (cell !== rowH) return null;
  if (cell < 16 || cell > 512) return null;
  return {
    columns: GRID_COLUMNS,
    rows: GRID_ROWS,
    frameWidth: cell,
    frameHeight: cell,
    regionX: cell,
    regionY: 0,
    sheetColumns: SHEET_COLUMNS,
    alignX: 0,
    alignY: 0,
  };
}

function trySixColumnSheet(width: number, height: number): CharacterGridSpec | null {
  if (width % GRID_COLUMNS !== 0 || height % GRID_ROWS !== 0) {
    return null;
  }
  const frameWidth = width / GRID_COLUMNS;
  const frameHeight = height / GRID_ROWS;
  if (frameWidth !== frameHeight) return null;
  if (frameWidth < 16 || frameWidth > 512) return null;
  return {
    columns: GRID_COLUMNS,
    rows: GRID_ROWS,
    frameWidth,
    frameHeight,
    regionX: 0,
    regionY: 0,
    sheetColumns: GRID_COLUMNS,
    alignX: 0,
    alignY: 0,
  };
}

const RELAXED_CELL_TOLERANCE_PX = 1.5;

function trySevenColumnSheetRelaxed(width: number, height: number): CharacterGridSpec | null {
  const cellW = width / SHEET_COLUMNS;
  const cellH = height / GRID_ROWS;
  if (Math.abs(cellW - cellH) > RELAXED_CELL_TOLERANCE_PX) return null;
  const cell = Math.round((cellW + cellH) / 2);
  if (cell < 16 || cell > 512) return null;
  return {
    columns: GRID_COLUMNS,
    rows: GRID_ROWS,
    frameWidth: cell,
    frameHeight: cell,
    regionX: cell,
    regionY: 0,
    sheetColumns: SHEET_COLUMNS,
    alignX: 0,
    alignY: 0,
  };
}

function trySixColumnSheetRelaxed(width: number, height: number): CharacterGridSpec | null {
  const cellW = width / GRID_COLUMNS;
  const cellH = height / GRID_ROWS;
  if (Math.abs(cellW - cellH) > RELAXED_CELL_TOLERANCE_PX) return null;
  const cell = Math.round((cellW + cellH) / 2);
  if (cell < 16 || cell > 512) return null;
  return {
    columns: GRID_COLUMNS,
    rows: GRID_ROWS,
    frameWidth: cell,
    frameHeight: cell,
    regionX: 0,
    regionY: 0,
    sheetColumns: GRID_COLUMNS,
    alignX: 0,
    alignY: 0,
  };
}

function inferGridFromImageSizeLenient(width: number, height: number): CharacterGridSpec {
  const strict =
    trySevenColumnSheet(width, height) ?? trySixColumnSheet(width, height);
  if (strict) return strict;
  const relaxed =
    trySevenColumnSheetRelaxed(width, height) ??
    trySixColumnSheetRelaxed(width, height);
  if (relaxed) return relaxed;
  return inferGridFromImageSize(width, height);
}

export function layoutWithAlign(
  layout: SheetLayout,
  alignX: number,
  alignY: number,
): SheetLayout {
  return {
    ...layout,
    grid: normalizeGridSpec({ ...layout.grid, alignX, alignY }),
  };
}

export function resolveSheetLayout(
  image: Pick<HTMLImageElement, "naturalWidth" | "naturalHeight">,
  tuning: GridTuning,
): SheetLayout {
  const base = sheetLayoutFromImageLenient(image);
  let grid = normalizeGridSpec({ ...base.grid });
  const { x: offsetX, y: offsetY } = tuning.offset;

  if (tuning.lockTemplateGrid) {
    const inferred = normalizeGridSpec({ ...grid });
    grid = normalizeGridSpec({
      ...inferred,
      columns: GRID_COLUMNS,
      rows: GRID_ROWS,
      alignX: offsetX,
      alignY: offsetY,
    });
  } else if (
    tuning.frameSize != null &&
    tuning.frameSize >= 16 &&
    tuning.frameSize <= 512
  ) {
    const size = Math.round(tuning.frameSize);
    const isSeven = grid.sheetColumns === SHEET_COLUMNS;
    grid = normalizeGridSpec({
      ...grid,
      frameWidth: size,
      frameHeight: size,
      regionX: isSeven ? size : 0,
      alignX: offsetX,
      alignY: offsetY,
    });
  } else {
    grid = normalizeGridSpec({ ...grid, alignX: offsetX, alignY: offsetY });
  }

  return { ...base, grid };
}

export function supportsTemplateGridLayout(
  width: number,
  height: number,
): boolean {
  return (
    trySevenColumnSheet(width, height) != null ||
    trySixColumnSheet(width, height) != null ||
    trySevenColumnSheetRelaxed(width, height) != null ||
    trySixColumnSheetRelaxed(width, height) != null
  );
}

/** 1 = 672×384, 2 = 1344×768, etc. */
export function templateScaleFactor(
  width: number,
  height: number,
): number | null {
  const seven = trySevenColumnSheet(width, height);
  if (seven) return seven.frameWidth / TEMPLATE_FRAME_WIDTH;
  const six = trySixColumnSheet(width, height);
  if (six) return six.frameWidth / TEMPLATE_FRAME_WIDTH;
  return null;
}

export function describeSheetLayout(layout: SheetLayout): {
  headline: string;
  warnings: string[];
} {
  const { grid, sheetWidth, sheetHeight } = layout;
  const warnings: string[] = [];
  const scale = templateScaleFactor(sheetWidth, sheetHeight);
  const integerScale =
    scale != null && scale >= 1 && Math.abs(scale - Math.round(scale)) < 0.001;
  const scaledScale = integerScale ? Math.round(scale!) : null;

  if (
    sheetWidth !== TEMPLATE_SHEET_WIDTH ||
    sheetHeight !== TEMPLATE_SHEET_HEIGHT
  ) {
    if (!integerScale) {
      warnings.push(
        `ขนาดภาพ ${sheetWidth}×${sheetHeight} px ไม่ตรงเทมเพลต ${TEMPLATE_SHEET_WIDTH}×${TEMPLATE_SHEET_HEIGHT} px`,
      );
    }
  }

  if (
    grid.frameWidth !== TEMPLATE_FRAME_WIDTH ||
    grid.frameHeight !== TEMPLATE_FRAME_HEIGHT
  ) {
    if (!integerScale || scaledScale === 1) {
      warnings.push(
        `เซลล์ ${grid.frameWidth}×${grid.frameHeight} px (เทมเพลต ${TEMPLATE_FRAME_WIDTH}×${TEMPLATE_FRAME_HEIGHT})`,
      );
    }
  }

  const expectedW =
    grid.sheetColumns === SHEET_COLUMNS
      ? grid.regionX + grid.columns * grid.frameWidth
      : grid.columns * grid.frameWidth;
  const expectedH = grid.rows * grid.frameHeight;
  if (
    Math.abs(expectedW - sheetWidth) > 2 ||
    Math.abs(expectedH - sheetHeight) > 2
  ) {
    warnings.push(
      `กริดครอบ ~${expectedW}×${expectedH} px แต่ภาพ ${sheetWidth}×${sheetHeight} px — ปรับ offset หรือขนาดเซลล์`,
    );
  }

  const colLabel =
    grid.sheetColumns === SHEET_COLUMNS
      ? `7 คอลัมน์ (ป้าย + ${GRID_COLUMNS} เฟรม)`
      : `${grid.sheetColumns} คอลัมน์สไปรต์`;
  const headline = `${sheetWidth}×${sheetHeight} px · ${colLabel} · เซลล์ ${grid.frameWidth}×${grid.frameHeight}`;

  return { headline, warnings };
}

/** Sheet-space X positions for vertical grid lines (matches sourceFrameRect). */
export function sheetVerticalLineXs(grid: CharacterGridSpec): number[] {
  const g = normalizeGridSpec(grid);
  const ax = g.alignX;
  if (g.sheetColumns === SHEET_COLUMNS) {
    const xs: number[] = [ax, ax + g.regionX];
    for (let c = 1; c <= g.columns; c += 1) {
      xs.push(ax + g.regionX + c * g.frameWidth);
    }
    return xs;
  }
  const xs: number[] = [ax];
  for (let c = 1; c <= g.sheetColumns; c += 1) {
    xs.push(ax + c * g.frameWidth);
  }
  return xs;
}

export function frameIndex(row: number, column: number, columns = GRID_COLUMNS): number {
  return row * columns + column;
}

export interface SheetLayout {
  grid: CharacterGridSpec;
  sheetWidth: number;
  sheetHeight: number;
}

export function sheetLayoutFromImage(
  image: Pick<HTMLImageElement, "naturalWidth" | "naturalHeight">,
): SheetLayout {
  const grid = inferGridFromImageSize(image.naturalWidth, image.naturalHeight);
  return {
    grid,
    sheetWidth: image.naturalWidth,
    sheetHeight: image.naturalHeight,
  };
}

/** Same as sheetLayoutFromImage but allows ~1px pitch drift (export rounding). */
export function sheetLayoutFromImageLenient(
  image: Pick<HTMLImageElement, "naturalWidth" | "naturalHeight">,
): SheetLayout {
  const grid = inferGridFromImageSizeLenient(image.naturalWidth, image.naturalHeight);
  return {
    grid,
    sheetWidth: image.naturalWidth,
    sheetHeight: image.naturalHeight,
  };
}

export function sheetCellWidth(layout: SheetLayout): number {
  return layout.sheetWidth / layout.grid.sheetColumns;
}

export function spriteRegionWidth(grid: CharacterGridSpec): number {
  return grid.frameWidth * grid.columns;
}

export function spriteRegionHeight(grid: CharacterGridSpec): number {
  return grid.frameHeight * grid.rows;
}

export function sheetToDisplayX(
  sheetX: number,
  sheetWidth: number,
  displayWidth: number,
): number {
  return (sheetX / sheetWidth) * displayWidth;
}

export function sheetToDisplayY(
  sheetY: number,
  sheetHeight: number,
  displayHeight: number,
): number {
  return (sheetY / sheetHeight) * displayHeight;
}

export function fitSheetToMaxWidth(
  sheetWidth: number,
  sheetHeight: number,
  maxWidth: number,
): { displayWidth: number; displayHeight: number } {
  const scale = Math.min(1, maxWidth / sheetWidth);
  const displayWidth = Math.round(sheetWidth * scale);
  const displayHeight = Math.round((displayWidth * sheetHeight) / sheetWidth);
  return { displayWidth, displayHeight };
}

export function rowSplitOffsetY(tuning: GridTuning | undefined, row: number): number {
  if (!tuning) return 0;
  const normalized = normalizeGridTuning(tuning);
  if (!normalized.rowSplit[row]) return 0;
  return normalized.rowOffsetY[row] ?? 0;
}

export function rowSplitOffsetX(tuning: GridTuning | undefined, row: number): number {
  if (!tuning) return 0;
  const normalized = normalizeGridTuning(tuning);
  if (!normalized.rowSplit[row]) return 0;
  return normalized.rowOffsetX[row] ?? 0;
}

export function hasAnyRowSplit(tuning: GridTuning | undefined): boolean {
  if (!tuning) return false;
  return normalizeGridTuning(tuning).rowSplit.some(Boolean);
}

export function sourceFrameRect(
  layout: SheetLayout,
  column: number,
  row: number,
  tuning?: GridTuning,
): { sx: number; sy: number; sw: number; sh: number } {
  const { grid } = layout;
  const extraY = rowSplitOffsetY(tuning, row);
  const extraX = rowSplitOffsetX(tuning, row);
  return {
    sx: grid.regionX + grid.alignX + column * grid.frameWidth + extraX,
    sy: grid.regionY + grid.alignY + row * grid.frameHeight + extraY,
    sw: grid.frameWidth,
    sh: grid.frameHeight,
  };
}
